import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';

import Progress from 'components/common/Progress';
import Button from 'components/common/Button';

import { fetchTotalStatistics } from 'redux/modules/totalStatistics';

import formatPrice from 'utils/formatPrice';

const styles = theme => ({
  root: {
    minHeight: 434,
    boxSizing: 'border-box',
    padding: 24,
  },
  gridChart: {
    position: 'relative',
    minHeight: 150,
    marginBottom: 24,
    marginLeft: -20,
    width: 'calc(100% + 20px)',
  },
  gridItemChart: {
    flexShrink: 1,
  },
  gridTable: {
    marginTop: 16,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  chartContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 143,
      height: 143,
      margin: '-76px 0 0 -76px',
      borderRadius: '50%',
      border: `4px solid ${theme.palette.primary.A100}`,
      zIndex: 1,
    },
  },
  chartLegend: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    zIndex: 2,
  },
  listItemIcon: {
    width: 10,
    height: 10,
    borderWidth: 2,
    borderStyle: 'solid',
    borderRadius: '50%',
    marginRight: 4,
  },
  listItemIconIntake: {
    borderColor: theme.palette.states.intake,
  },
  listItemIconDelivery: {
    borderColor: theme.palette.states.delivery,
  },
  listItemIconBilling: {
    borderColor: theme.palette.states.billing,
  },
  listItemIconCollection: {
    borderColor: theme.palette.states.collection,
  },
  listItemIconPaid: {
    borderColor: theme.palette.states.paid,
  },
  button: {
    marginTop: 24,
    minWidth: 180,
  },
  gridItemShrink: {
    flexShrink: 1,
  },
});

class StatsDoughnutChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      Doughnut: null,
    };

    this.importDependencies = this.importDependencies.bind(this);
  }

  componentDidMount() {
    this.importDependencies();
    this.props.actions.fetchTotalStatistics({
      triggerLoading: true,
    });
  }

  async importDependencies() {
    const { Doughnut } = await import('react-chartjs-2');

    this.setState({
      Doughnut,
    });
  }

  render() {
    const { totalStatistics, loading, theme, classes } = this.props;
    const { Doughnut } = this.state;
    return (
      <Paper className={classes.root}>
        <Grid
          className={classes.gridChart}
          container
          spacing={0}
          alignItems="center"
          justify="space-between"
          wrap="nowrap"
        >
          {!Doughnut || loading > 0 ? (
            <Progress />
          ) : (
            <Fragment>
              <Grid
                className={classes.gridItemChart}
                item
                xs={12}
              >
                <div className={classes.chartContainer}>
                  <div className={classes.chartLegend}>
                    <Typography
                      variant="headline"
                      align="center"
                    >
                      {(totalStatistics.Intake || 0)
                        + (totalStatistics.Delivery || 0)
                        + (totalStatistics.Billing || 0)
                        + (totalStatistics.Collection || 0)
                        + (totalStatistics.Paid || 0)}
                    </Typography>
                    <Typography
                      align="center"
                      variant="caption"
                    >
                      orders
                    </Typography>
                  </div>
                  <Doughnut
                    width={150}
                    height={150}
                    data={{
                      labels: [
                        'Intake',
                        'Delivery',
                        'Billing',
                        'Collection',
                        'Paid',
                      ],
                      datasets: [{
                        data: [
                          totalStatistics.Intake,
                          totalStatistics.Delivery,
                          totalStatistics.Billing,
                          totalStatistics.Collection,
                          totalStatistics.Paid,
                        ],
                        backgroundColor: [
                          theme.palette.states.intake,
                          theme.palette.states.delivery,
                          theme.palette.states.billing,
                          theme.palette.states.collection,
                          theme.palette.states.paid,
                        ],
                        borderWidth: [2, 2, 2, 2, 2],
                        borderColor: [
                          theme.palette.common.white,
                          theme.palette.common.white,
                          theme.palette.common.white,
                          theme.palette.common.white,
                          theme.palette.common.white,
                        ],
                        hoverBorderColor: [
                          theme.palette.common.white,
                          theme.palette.common.white,
                          theme.palette.common.white,
                          theme.palette.common.white,
                          theme.palette.common.white,
                        ],
                        hoverBackgroundColor: [
                          theme.palette.states.intake,
                          theme.palette.states.delivery,
                          theme.palette.states.billing,
                          theme.palette.states.collection,
                          theme.palette.states.paid,
                        ],
                      }],
                    }}
                    options={{
                      responsive: false,
                      width: 150,
                      height: 150,
                      cutoutPercentage: 83,
                      events: [],
                    }}
                    legend={{
                      display: false,
                    }}
                  />
                </div>
              </Grid>
              <Grid item>
                <List>
                  <ListItem
                    dense
                    disableGutters
                  >
                    <div
                      className={classNames(classes.listItemIcon,
                        classes.listItemIconIntake)}
                    />
                    <Typography variant="caption">
                      Intake {totalStatistics.Intake}
                    </Typography>
                  </ListItem>
                  <ListItem
                    dense
                    disableGutters
                  >
                    <div
                      className={classNames(classes.listItemIcon,
                        classes.listItemIconDelivery)}
                    />
                    <Typography variant="caption">
                      Delivery {totalStatistics.Delivery}
                    </Typography>
                  </ListItem>
                  <ListItem
                    dense
                    disableGutters
                  >
                    <div
                      className={classNames(classes.listItemIcon,
                        classes.listItemIconBilling)}
                    />
                    <Typography variant="caption">
                      Billing {totalStatistics.Billing}
                    </Typography>
                  </ListItem>
                  <ListItem
                    dense
                    disableGutters
                  >
                    <div
                      className={classNames(classes.listItemIcon,
                        classes.listItemIconCollection)}
                    />
                    <Typography variant="caption">
                      Collection {totalStatistics.Collection}
                    </Typography>
                  </ListItem>
                  <ListItem
                    dense
                    disableGutters
                  >
                    <div
                      className={classNames(classes.listItemIcon,
                        classes.listItemIconPaid)}
                    />
                    <Typography variant="caption">
                      Paid {totalStatistics.Paid}
                    </Typography>
                  </ListItem>
                </List>
              </Grid>
            </Fragment>
          )}
        </Grid>
        <Grid
          className={classes.gridTable}
          container
          spacing={8}
          alignItems="flex-start"
          justify="space-between"
          wrap="nowrap"
        >
          <Grid
            className={classes.gridItemShrink}
            item
          >
            <Typography variant="caption">
              Inventory
            </Typography>
            <Typography color="secondary">
              Products in storage
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="title">
              {formatPrice(totalStatistics['Products in storage'])}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          className={classes.gridTable}
          container
          spacing={8}
          alignItems="center"
          justify="space-between"
        >
          <Grid item>
            <Typography variant="caption">
              Work Flow
            </Typography>
            <Typography color="secondary">
              Back order products
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="title">
              {totalStatistics['Back order products']}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={16}
          alignItems="center"
          justify="center"
        >
          <Grid item>
            <Button
              className={classes.button}
              variant="raised"
              href="/inventory"
            >
              Add New Items
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  }
}

StatsDoughnutChart.propTypes = {
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({
        500: PropTypes.string.isRequired,
        A100: PropTypes.string.isRequired,
        A400: PropTypes.string.isRequired,
      }).isRequired,
      grey: PropTypes.shape({
        600: PropTypes.string.isRequired,
      }).isRequired,
      common: PropTypes.shape({
        white: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  totalStatistics: PropTypes.shape({
    'Products in storage': PropTypes.number,
    Intake: PropTypes.number,
    'Back order products': PropTypes.number,
    Collection: PropTypes.number,
    Delivery: PropTypes.number,
    Billing: PropTypes.number,
    Paid: PropTypes.number,
  }).isRequired,
  loading: PropTypes.number.isRequired,
  actions: PropTypes.shape({
    fetchTotalStatistics: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    gridChart: PropTypes.string.isRequired,
    gridItemChart: PropTypes.string.isRequired,
    gridTable: PropTypes.string.isRequired,
    chartContainer: PropTypes.string.isRequired,
    chartLegend: PropTypes.string.isRequired,
    listItemIcon: PropTypes.string.isRequired,
    listItemIconIntake: PropTypes.string.isRequired,
    listItemIconDelivery: PropTypes.string.isRequired,
    listItemIconBilling: PropTypes.string.isRequired,
    listItemIconCollection: PropTypes.string.isRequired,
    gridItemShrink: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = store => ({
  totalStatistics: store.totalStatistics.data,
  loading: store.totalStatistics.loading,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ fetchTotalStatistics }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles, { withTheme: true })(StatsDoughnutChart),
);



// WEBPACK FOOTER //
// ./src/components/home/StatsDoughnutChart/StatsDoughnutChart.js